import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import type {
  EarlyOutReason,
  Employee,
  EmployeeSession,
  PunchActionType,
  PunchCorrectionReason,
  PunchEvent,
} from '../../types/employee';
import {
  buildDayRangeReplacePlan,
  canPunch,
  createCorrectionPunchEvent,
  createPunchEvent,
  createVoidEvent,
  formatPunchDate,
  isEventVoided,
  todayDateKey,
  validateAddMissingPlan,
  validateCorrectionPlan,
  validateDayRangeOpsPlan,
  type AddMissingPunch,
} from '../../services/punchService';
import { employeeIdAliases } from '../../lib/employeeIds';
import type { CorrectionResult, PunchResult } from './workforceTypes';

export function useWorkforcePunchActions(
  session: EmployeeSession | null,
  employees: Employee[],
  punchEvents: PunchEvent[],
  setPunchEvents: Dispatch<SetStateAction<PunchEvent[]>>
) {
  /**
   * Reads must match either the Convex id or the pre-migration `teacher-N`
   * legacyId, since existing punch events are still keyed by the latter.
   */
  const sessionIdKey = useMemo(() => {
    if (!session) return null;
    const employee = employees.find((e) => e.id === session.employeeId);
    return employee ? employeeIdAliases(employee) : [session.employeeId];
  }, [session, employees]);

  const punch = useCallback(
    (type: PunchActionType, opts?: { earlyOutReason?: EarlyOutReason }): PunchResult => {
      if (!session || session.channel !== 'office') {
        return { ok: false, error: 'unauthorized' };
      }

      const idKey = sessionIdKey ?? session.employeeId;
      const check = canPunch(punchEvents, idKey, type);
      if (!check.ok) return { ok: false, error: check.reason };

      const event = createPunchEvent(
        session.employeeId,
        type,
        undefined,
        type === 'out' && opts?.earlyOutReason
          ? { earlyOutReason: opts.earlyOutReason }
          : undefined
      );
      setPunchEvents((prev) => [...prev, event]);
      return { ok: true, event };
    },
    [session, sessionIdKey, punchEvents, setPunchEvents]
  );

  const validateCorrection = useCallback(
    (
      approvedByEmployeeId: string,
      reason: PunchCorrectionReason,
      reasonNote?: string
    ): CorrectionResult | null => {
      if (!session || session.channel !== 'office') {
        return { ok: false, error: 'unauthorized' };
      }
      if (!reason) return { ok: false, error: 'missing-reason' };
      if (reason === 'other' && !reasonNote?.trim()) {
        return { ok: false, error: 'missing-reason' };
      }
      if (!approvedByEmployeeId) return { ok: false, error: 'missing-approver' };
      if (approvedByEmployeeId === session.employeeId) {
        return { ok: false, error: 'self-approval' };
      }
      const approver = employees.find((e) => e.id === approvedByEmployeeId);
      if (!approver || approver.status !== 'active') {
        return { ok: false, error: 'missing-approver' };
      }
      return null;
    },
    [session, employees]
  );

  const voidPunch = useCallback(
    (
      voidsEventId: string,
      reason: PunchCorrectionReason,
      approvedByEmployeeId: string,
      reasonNote?: string
    ): CorrectionResult => {
      const validation = validateCorrection(approvedByEmployeeId, reason, reasonNote);
      if (validation) return validation;

      const target = punchEvents.find((e) => e.id === voidsEventId);
      if (!target || target.employeeId !== session!.employeeId || target.type === 'void') {
        return { ok: false, error: 'invalid-target' };
      }
      if (isEventVoided(punchEvents, voidsEventId)) {
        return { ok: false, error: 'already-voided' };
      }

      const sequence = validateCorrectionPlan(
        punchEvents,
        session!.employeeId,
        {
          mode: 'void',
          voidEventId: voidsEventId,
        },
        formatPunchDate(target.at)
      );
      if (!sequence.ok) return { ok: false, error: sequence.reason };

      const event = createVoidEvent(
        session!.employeeId,
        voidsEventId,
        reason,
        approvedByEmployeeId,
        reasonNote
      );
      setPunchEvents((prev) => [...prev, event]);
      return { ok: true, event };
    },
    [session, punchEvents, validateCorrection, setPunchEvents]
  );

  const addCorrectionPunch = useCallback(
    (
      type: PunchActionType,
      at: string,
      reason: PunchCorrectionReason,
      approvedByEmployeeId: string,
      reasonNote?: string
    ): CorrectionResult => {
      const validation = validateCorrection(approvedByEmployeeId, reason, reasonNote);
      if (validation) return validation;

      const sequence = validateCorrectionPlan(
        punchEvents,
        session!.employeeId,
        {
          mode: 'add',
          type,
          at,
        },
        formatPunchDate(at)
      );
      if (!sequence.ok) return { ok: false, error: sequence.reason };

      const event = createCorrectionPunchEvent(
        session!.employeeId,
        type,
        at,
        reason,
        approvedByEmployeeId,
        reasonNote
      );
      setPunchEvents((prev) => [...prev, event]);
      return { ok: true, event };
    },
    [session, punchEvents, validateCorrection, setPunchEvents]
  );

  const addMissingPunches = useCallback(
    (punches: AddMissingPunch[], approvedByEmployeeId: string): CorrectionResult => {
      const validation = validateCorrection(approvedByEmployeeId, 'forgot-punch', undefined);
      if (validation) return validation;

      const dateKey = punches[0] ? formatPunchDate(punches[0].at) : todayDateKey();
      const sequence = validateAddMissingPlan(punchEvents, session!.employeeId, punches, dateKey);
      if (!sequence.ok) return { ok: false, error: sequence.reason };

      const newEvents = punches.map((punch) =>
        createCorrectionPunchEvent(
          session!.employeeId,
          punch.type,
          punch.at,
          'forgot-punch',
          approvedByEmployeeId
        )
      );
      setPunchEvents((prev) => [...prev, ...newEvents]);
      return { ok: true, event: newEvents[newEvents.length - 1]! };
    },
    [session, punchEvents, validateCorrection, setPunchEvents]
  );

  const replacePunch = useCallback(
    (
      voidsEventId: string,
      type: PunchActionType,
      at: string,
      reason: PunchCorrectionReason,
      approvedByEmployeeId: string,
      reasonNote?: string
    ): CorrectionResult => {
      const validation = validateCorrection(approvedByEmployeeId, reason, reasonNote);
      if (validation) return validation;

      const target = punchEvents.find((e) => e.id === voidsEventId);
      if (!target || target.employeeId !== session!.employeeId || target.type === 'void') {
        return { ok: false, error: 'invalid-target' };
      }
      if (isEventVoided(punchEvents, voidsEventId)) {
        return { ok: false, error: 'already-voided' };
      }

      const dateKey = formatPunchDate(target.at);
      const sequence = validateCorrectionPlan(
        punchEvents,
        session!.employeeId,
        {
          mode: 'replace',
          voidEventId: voidsEventId,
          type,
          at,
        },
        dateKey
      );
      if (!sequence.ok) return { ok: false, error: sequence.reason };

      const voidEvent = createVoidEvent(
        session!.employeeId,
        voidsEventId,
        reason,
        approvedByEmployeeId,
        reasonNote
      );
      const replacementEvent = createCorrectionPunchEvent(
        session!.employeeId,
        type,
        at,
        reason,
        approvedByEmployeeId,
        reasonNote
      );
      setPunchEvents((prev) => [...prev, voidEvent, replacementEvent]);
      return { ok: true, event: voidEvent, replacementEvent };
    },
    [session, punchEvents, validateCorrection, setPunchEvents]
  );

  const replaceDayPunchRange = useCallback(
    (
      dateKey: string,
      inTime: string,
      outTime: string,
      reason: PunchCorrectionReason,
      approvedByEmployeeId: string,
      reasonNote?: string
    ): CorrectionResult => {
      const validation = validateCorrection(approvedByEmployeeId, reason, reasonNote);
      if (validation) return validation;

      const ops = buildDayRangeReplacePlan(
        punchEvents,
        session!.employeeId,
        dateKey,
        inTime,
        outTime
      );
      const sequence = validateDayRangeOpsPlan(
        punchEvents,
        session!.employeeId,
        dateKey,
        ops
      );
      if (!sequence.ok) return { ok: false, error: sequence.reason };

      const newEvents: PunchEvent[] = [];
      for (const op of ops) {
        if (op.kind === 'void') {
          newEvents.push(
            createVoidEvent(session!.employeeId, op.eventId, reason, approvedByEmployeeId, reasonNote)
          );
        } else if (op.kind === 'replace') {
          newEvents.push(
            createVoidEvent(session!.employeeId, op.eventId, reason, approvedByEmployeeId, reasonNote)
          );
          newEvents.push(
            createCorrectionPunchEvent(
              session!.employeeId,
              op.type,
              op.at,
              reason,
              approvedByEmployeeId,
              reasonNote
            )
          );
        } else {
          newEvents.push(
            createCorrectionPunchEvent(
              session!.employeeId,
              op.type,
              op.at,
              reason,
              approvedByEmployeeId,
              reasonNote
            )
          );
        }
      }

      setPunchEvents((prev) => [...prev, ...newEvents]);
      return { ok: true, event: newEvents[newEvents.length - 1]! };
    },
    [session, punchEvents, validateCorrection, setPunchEvents]
  );

  return {
    punch,
    voidPunch,
    addCorrectionPunch,
    addMissingPunches,
    replacePunch,
    replaceDayPunchRange,
  };
}
